const MAX_TRACKED_SIGNATURES = 100;
const DEDUP_TTL_MS = 60_000;
const RATE_LIMIT_WINDOW_MS = 60_000;
const MAX_REPORTS_PER_WINDOW = 30;
const MAX_INFLIGHT_REPORTS = 2;

/**
 * Bounded, isomorphic gate shared by the browser and server error reporters.
 *
 * **Role.** A `ReportGuard` is the single decision point for "should this
 * error actually be sent right now." It has no knowledge of HTTP, DOM, or
 * Node — it only tracks signatures, timestamps, and an in-flight counter, so
 * it can run unchanged in the browser bundle and the server bundle.
 *
 * **Responsibility.**
 * - Owns: a bounded (max 100 entries) signature → expiry map for 60s dedup,
 *   a 60s sliding-window counter capped at 30 reports, and an in-flight
 *   counter capped at 2 concurrent sends.
 * - Does NOT own: building the report payload, sending it, or deciding
 *   whether an error is reportable in the first place (aborted/expected 4xx
 *   filtering happens in the caller).
 *
 * @example
 * const guard = new ReportGuard();
 * if (guard.tryAcquire(signature)) {
 *   try { await send(); } finally { guard.release(); }
 * }
 */
export class ReportGuard {
  private readonly seenSignatures = new Map<string, number>();
  private readonly reportTimestamps: number[] = [];
  private inFlightCount = 0;

  /**
   * Purpose: decide whether a report with this signature may be sent now,
   * reserving dedup/rate/inflight capacity when it returns true. The caller
   * must call `release()` once the send settles (success, failure, or
   * timeout) when this returns true.
   */
  public tryAcquire(signature: string): boolean {
    const now = Date.now();

    this.evictExpiredSignatures(now);

    if (this.seenSignatures.has(signature)) return false;

    if (this.inFlightCount >= MAX_INFLIGHT_REPORTS) return false;

    this.pruneRateWindow(now);

    if (this.reportTimestamps.length >= MAX_REPORTS_PER_WINDOW) return false;

    this.rememberSignature(signature, now);
    this.reportTimestamps.push(now);
    this.inFlightCount += 1;

    return true;
  }

  /**
   * Purpose: release the in-flight slot reserved by a prior `tryAcquire`
   * call. Safe to call even if the send failed or timed out.
   */
  public release(): void {
    if (this.inFlightCount > 0) {
      this.inFlightCount -= 1;
    }
  }

  /**
   * Purpose: record a signature with its dedup expiry, evicting the oldest
   * tracked entry first when the bounded map is already full.
   */
  private rememberSignature(signature: string, now: number): void {
    if (this.seenSignatures.size >= MAX_TRACKED_SIGNATURES) {
      const oldestSignature = this.seenSignatures.keys().next().value;

      if (oldestSignature !== undefined) {
        this.seenSignatures.delete(oldestSignature);
      }
    }

    this.seenSignatures.set(signature, now + DEDUP_TTL_MS);
  }

  /**
   * Purpose: drop signatures whose 60s TTL has elapsed so the bounded map
   * keeps reflecting only recently-seen errors.
   */
  private evictExpiredSignatures(now: number): void {
    for (const [signature, expiresAt] of this.seenSignatures) {
      if (expiresAt <= now) {
        this.seenSignatures.delete(signature);
      }
    }
  }

  /**
   * Purpose: drop timestamps outside the 60s sliding rate-limit window.
   */
  private pruneRateWindow(now: number): void {
    while (
      this.reportTimestamps.length > 0 &&
      now - this.reportTimestamps[0] > RATE_LIMIT_WINDOW_MS
    ) {
      this.reportTimestamps.shift();
    }
  }
}
